import { FC, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";

import AddButton from "@/components/AddButton";
import { SrOnly } from "@/components/Layout/Parts";
import { Table, TableContent, Th } from "@/components/Table";
import ToggableHeading from "@/components/ToggableHeading";
import { AddButtonHolder } from "./Notes";
import ShoppingListTableRow from "./ShoppingListTableRow";
import ShoppingListModal from "./ShoppingListModal";
import { ProductionSceneShoppingItemResource } from "@/types/resources";

const StyledTable = styled(Table)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  th,
  td {
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: calc(30% - 56px);
      padding-left: 24px;
    }
    &:nth-child(2),
    &:nth-child(4) {
      width: 24%;
    }
    &:nth-child(3) {
      width: 22%;
    }
    &:last-child {
      width: 56px;
      position: relative;
      padding-right: 16px;
    }
  }
  td {
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
  }
`;

const TableHolder = styled.div`
  margin-bottom: 56px;
`;

interface Props {
  shoppingItems: ProductionSceneShoppingItemResource[][];
  scenes: number[];
}

const ShoppingListTable: FC<Props> = ({ shoppingItems, scenes }) => {
  const [isOpen, setIsOpen] = useState(true);

  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <ToggableHeading
        heading="SHOPPING LIST"
        onToggle={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
      />
      {isOpen && (
        <TableHolder>
          <StyledTable $fixed>
            <thead>
              <tr>
                <Th>Scene Number & Element</Th>
                <Th>Item Info</Th>
                <Th>Assignee</Th>
                <Th>Note</Th>
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={false}
                colSpan={5}
                isError={false}
                hasData={true}
              >
                {shoppingItems.map((sceneItems, index) =>
                  sceneItems.map((ShoppingItem) => (
                    <ShoppingListTableRow
                      key={ShoppingItem.id}
                      sceneIndex={index + 1}
                      shoppingItem={ShoppingItem}
                      scenes={scenes}
                    />
                  ))
                )}
              </TableContent>
            </tbody>
          </StyledTable>
          <AddButtonHolder>
            <AddButton onClick={() => setShowModal(true)}>Add item</AddButton>
          </AddButtonHolder>
          <AnimatePresence>
            {!!showModal && (
              <ShoppingListModal
                scenes={scenes}
                onClose={() => setShowModal(false)}
              />
            )}
          </AnimatePresence>
        </TableHolder>
      )}
    </>
  );
};

export default ShoppingListTable;
